import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Construction, ArrowLeft, Phone, ChevronLeft, ChevronRight, X, ZoomIn, FileText } from 'lucide-react';
import { ksbEquipmentData } from '../../data/ksb';
import './KSBSubpage.css';

const KSBEquipmentPage = () => {
  const { category, equipment } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=¡Hola!%20Quiero%20consultar%20sobre%20KSB`;

  // Build the key to lookup in ksbEquipmentData
  let dataKey;
  if (equipment) {
    dataKey = `${category}/${equipment}`;
  } else if (category) {
    dataKey = category;
  } else {
    const pathParts = location.pathname.split('/').filter(x => x);
    if (pathParts[0] === 'ksb' && pathParts[1]) {
      dataKey = pathParts[1];
    }
  }

  const data = ksbEquipmentData[dataKey];
  const images = data ? (data.images || []) : [];
  const hasImages = images.length > 0;

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      if (e.key === 'ArrowRight') setSelectedImage(prev => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setSelectedImage(prev => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isLightboxOpen, images.length]);

  const openLightbox = (index) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };

  if (!data) {
    return (
      <div className="ksb-construction-page" data-testid="ksb-not-found">
        <section className="ksb-construction-hero">
          <h1 className="ksb-construction-hero-title">Página no encontrada</h1>
          <p className="ksb-construction-hero-description">
            El contenido que busca no está disponible.
          </p>
        </section>
        <div className="ksb-construction-content">
          <div className="ksb-construction-box">
            <Button
              onClick={() => navigate('/ksb')}
              className="ksb-construction-button"
            >
              <ArrowLeft className="mr-2" size={18} />
              Volver a KSB
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Determine back navigation
  const getBackPath = () => {
    if (data.category === 'bombas') return '/ksb/bombas';
    if (data.category === 'valvulas') return '/ksb/valvulas';
    return '/ksb';
  };

  const getBackLabel = () => {
    return data.categoryTitle || 'KSB';
  };

  // Under construction fallback
  if (data.isUnderConstruction) {
    return (
      <div className="ksb-construction-page" data-testid="ksb-equipment-page">
        <section className="ksb-construction-hero">
          <h1 className="ksb-construction-hero-title">{data.title}</h1>
          <p className="ksb-construction-hero-description">{data.description}</p>
        </section>
        {data.referencesHeading && (
          <div className="ksb-references-header">
            <span className="ksb-references-line" />
            <h2 className="ksb-references-title">{data.referencesHeading}</h2>
            <span className="ksb-references-line" />
          </div>
        )}
        <div className="ksb-construction-content">
          <div className="ksb-construction-box">
            <Construction size={64} className="ksb-construction-icon" />
            <h2 className="ksb-construction-title">Página en construcción</h2>
            <p className="ksb-construction-text">
              Estamos trabajando en el contenido de esta sección.
              Mientras tanto, podés contactarnos para más información.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Button
                onClick={() => window.open(whatsappLink, '_blank')}
                className="ksb-construction-button"
                data-testid="construction-contact-button"
              >
                <Phone className="mr-2" size={18} />
                Contactanos
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(getBackPath())}
              >
                <ArrowLeft className="mr-2" size={18} />
                Volver a {getBackLabel()}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ksb-construction-page" data-testid="ksb-equipment-page">
      {/* Hero Section */}
      <section className="ksb-construction-hero">
        <h1 className="ksb-construction-hero-title">{data.title}</h1>
        <p className="ksb-construction-hero-description">{data.description}</p>
      </section>

      {/* PDF Button */}
      {data.pdfHref && (
        <div className="ksb-pdf-section">
          <a
            href={data.pdfHref}
            target="_blank"
            rel="noopener noreferrer"
            className="ksb-pdf-button"
          >
            <FileText size={18} />
            Ver catálogo PDF
          </a>
        </div>
      )}

      {/* References heading */}
      {data.referencesHeading && (
        <div className="ksb-references-header">
          <span className="ksb-references-line" />
          <h2 className="ksb-references-title">{data.referencesHeading}</h2>
          <span className="ksb-references-line" />
        </div>
      )}

      {/* Gallery Section */}
      {hasImages && (
        <section className="ksb-gallery-section">
          <div className="ksb-gallery-container">
            <div className="masonry-grid">
              {images.map((img, index) => (
                <div
                  key={index}
                  className="masonry-item"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={img}
                    alt={`${data.title} - Imagen ${index + 1}`}
                    className="masonry-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="masonry-overlay">
                    <ZoomIn size={26} strokeWidth={1.5} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="ksb-equipment-cta-section">
        <div className="ksb-equipment-cta-container">
          <p className="ksb-equipment-cta-text">
            ¿Necesitás más información sobre {data.title.toLowerCase()}?
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => window.open(whatsappLink, '_blank')}
              className="ksb-construction-button"
            >
              <Phone className="mr-2" size={18} />
              Contactanos
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(getBackPath())}
              style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
            >
              <ArrowLeft className="mr-2" size={18} />
              Volver a {getBackLabel()}
            </Button>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setIsLightboxOpen(false)}>
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="lightbox-close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>

            <div className="lightbox-image-area">
              {images.length > 1 && (
                <button
                  className="lightbox-nav prev"
                  onClick={() => setSelectedImage(prev => (prev - 1 + images.length) % images.length)}
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft size={28} />
                </button>
              )}
              <img
                key={`lb-${selectedImage}`}
                src={images[selectedImage]}
                alt={`${data.title} - Imagen ${selectedImage + 1}`}
                className="lightbox-image"
              />
              {images.length > 1 && (
                <button
                  className="lightbox-nav next"
                  onClick={() => setSelectedImage(prev => (prev + 1) % images.length)}
                  aria-label="Siguiente imagen"
                >
                  <ChevronRight size={28} />
                </button>
              )}
            </div>

            <div className="lightbox-footer">
              <span className="lightbox-counter">{selectedImage + 1} / {images.length}</span>
              {images.length > 1 && (
                <div className="lightbox-thumbs">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      className={`lightbox-thumb${index === selectedImage ? ' active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                      aria-label={`Ver imagen ${index + 1}`}
                    >
                      <img
                        src={img}
                        alt=""
                        className="lightbox-thumb-img"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KSBEquipmentPage;
